'use client';

/* eslint-disable react-hooks/refs -- deliberate "ref-guarded render-time setState" pattern (see each call site); permitted by React but not understood by react-hooks/refs. */

import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Popover,
  PopoverTrigger,
  PopoverContent
} from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import {
  getSmokeballMatterTypes,
  searchSmokeballMatterTypes,
  getSmokeballClientRoles,
  type MatterType
} from '@/lib/services/integrations/smokeball';
import { AUSTRALIAN_STATES } from '@/lib/constants';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { useLawFirmProfile } from '@/lib/services/account';

export interface SmokeballSyncConfig {
  stateOfMatter: string;
  matterTypeId: string;
  representativeOption?: string;
}

export interface SmokeballSyncFieldsProps {
  matterTypeSlug: string;
  initialConfig?: {
    stateOfMatter?: string;
    matterTypeId?: string;
    representativeOption?: string;
  };
  onConfigChange: (config: SmokeballSyncConfig | null) => void;
  onIntegrationStatus?: (enabled: boolean) => void;
  /**
   * Whether the matter-type picker has anything to offer, reported once the
   * catalogue has loaded. A consumer that renders a *panel* around these fields
   * must gate its visibility on this: an enabled integration is not the same as
   * a selectable one, and a panel offering an empty picker cannot be completed
   * or dismissed (VALE-1105).
   */
  onHasSelectableMatterTypes?: (hasSelectable: boolean) => void;
  /** LPR hints for estate-admin auto-select (detail mode only) */
  lprAutoSelectHint?: { hasExecutor: boolean; hasAdmin: boolean };
  /** Override default state (e.g. from decedent address) */
  defaultState?: string;
  /** Styling variant */
  variant?: 'detail' | 'create';
}

/* -------------------------------------------------------------------------- */
/* Hooks — each owns one network-driven side effect                           */
/* -------------------------------------------------------------------------- */

type IntegrationStatus = 'loading' | 'enabled' | 'disabled' | 'error';

interface IntegrationState {
  status: IntegrationStatus;
  matterTypes: MatterType[];
}

function useSmokeballIntegration(matterTypeSlug: string): IntegrationState {
  const isDynamicMatterType = matterTypeSlug === 'other';
  const [state, setState] = useState<IntegrationState>({
    status: 'loading',
    matterTypes: []
  });

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!matterTypeSlug) {
        if (!cancelled) setState({ status: 'disabled', matterTypes: [] });
        return;
      }

      setState((prev) => ({ ...prev, status: 'loading' }));

      if (isDynamicMatterType) {
        const result = await searchSmokeballMatterTypes();
        if (cancelled) return;
        if (result.success) {
          setState({ status: 'enabled', matterTypes: [] });
        } else if (result.statusCode === 409) {
          setState({ status: 'disabled', matterTypes: [] });
        } else {
          setState({ status: 'error', matterTypes: [] });
        }
        return;
      }

      const result = await getSmokeballMatterTypes(matterTypeSlug);
      if (cancelled) return;
      if (result.success && result.data) {
        setState({
          status: 'enabled',
          matterTypes: result.data.matterTypes
        });
      } else if (result.statusCode === 409) {
        setState({ status: 'disabled', matterTypes: [] });
      } else {
        setState({ status: 'error', matterTypes: [] });
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [matterTypeSlug, isDynamicMatterType]);

  return state;
}

interface MatterTypeSearchState {
  results: MatterType[];
  isSearching: boolean;
}

function useSmokeballMatterTypeSearch({
  enabled,
  query,
  location
}: {
  enabled: boolean;
  query: string;
  location: string;
}): MatterTypeSearchState {
  const [state, setState] = useState<MatterTypeSearchState>({
    results: [],
    isSearching: false
  });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    async function run() {
      setState((prev) => ({ ...prev, isSearching: true }));
      const result = await searchSmokeballMatterTypes({
        search: query || undefined,
        location: location || undefined
      });
      if (cancelled) return;
      setState({
        results: result.success && result.data ? result.data.matterTypes : [],
        isSearching: false
      });
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [enabled, query, location]);

  return state;
}

interface ClientRolesState {
  roles: string[];
  isLoading: boolean;
}

function useSmokeballClientRoles(matterTypeId: string): ClientRolesState {
  const { data, isFetching } = useQuery({
    queryKey: ['smokeball-client-roles', matterTypeId],
    enabled: !!matterTypeId,
    queryFn: async () => {
      const result = await getSmokeballClientRoles(matterTypeId);
      return result.success && result.data ? result.data.clientRoles : [];
    }
  });

  return {
    roles: matterTypeId ? (data ?? []) : [],
    isLoading: !!matterTypeId && isFetching
  };
}

/* -------------------------------------------------------------------------- */
/* Form reducer                                                                */
/* -------------------------------------------------------------------------- */

interface FormState {
  /** `null` = user has not touched the field; derive from default in render. */
  userSelectedStateOfMatter: string | null;
  matterTypeId: string;
  /** Display name for the dynamic-search selection. */
  matterTypeName: string;
  representativeOption: string;
}

type FormAction =
  | { type: 'SET_STATE_OF_MATTER'; value: string }
  | { type: 'SET_MATTER_TYPE'; id: string; name?: string }
  | { type: 'SET_REPRESENTATIVE_OPTION'; value: string };

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_STATE_OF_MATTER':
      return { ...state, userSelectedStateOfMatter: action.value };
    case 'SET_MATTER_TYPE':
      return {
        ...state,
        matterTypeId: action.id,
        matterTypeName: action.name ?? state.matterTypeName
      };
    case 'SET_REPRESENTATIVE_OPTION':
      return { ...state, representativeOption: action.value };
  }
}

function createInitialFormState(
  initialConfig: SmokeballSyncFieldsProps['initialConfig']
): FormState {
  return {
    userSelectedStateOfMatter: initialConfig?.stateOfMatter ?? null,
    matterTypeId: initialConfig?.matterTypeId ?? '',
    matterTypeName: '',
    representativeOption: initialConfig?.representativeOption ?? ''
  };
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

export function SmokeballSyncFields({
  matterTypeSlug,
  initialConfig,
  onConfigChange,
  onIntegrationStatus,
  onHasSelectableMatterTypes,
  lprAutoSelectHint,
  defaultState,
  variant = 'detail'
}: SmokeballSyncFieldsProps) {
  const { profile: lawFirmProfile } = useLawFirmProfile();
  const isDynamicMatterType = matterTypeSlug === 'other';

  const integration = useSmokeballIntegration(matterTypeSlug);

  const [form, dispatchForm] = useReducer(
    formReducer,
    initialConfig,
    createInitialFormState
  );

  // `lawFirmProfile?.businessAddress?.state` may arrive after first render, so
  // we read it on every render and fall back to it when the user has not
  // explicitly chosen a value yet.
  const effectiveDefaultState =
    defaultState || lawFirmProfile?.businessAddress?.state || '';
  const selectedStateOfMatter =
    form.userSelectedStateOfMatter ?? effectiveDefaultState;

  // Dynamic-search UI
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const search = useSmokeballMatterTypeSearch({
    enabled: isDynamicMatterType && integration.status === 'enabled',
    query: debouncedSearchQuery,
    location: selectedStateOfMatter
  });

  const clientRoles = useSmokeballClientRoles(form.matterTypeId);

  // Auto-select "Client" when it's the only role available. Done as a "store
  // info from previous renders" guard so the change lands on the same render
  // the roles arrive, avoiding an extra commit + downstream emit cycle.
  const [autoClientRoleKey, setAutoClientRoleKey] = useState<string | null>(
    null
  );
  const rolesSignature = clientRoles.roles.join('|');
  if (autoClientRoleKey !== rolesSignature) {
    setAutoClientRoleKey(rolesSignature);
    if (
      clientRoles.roles.length === 1 &&
      clientRoles.roles[0] === 'Client' &&
      form.representativeOption !== 'Client'
    ) {
      dispatchForm({ type: 'SET_REPRESENTATIVE_OPTION', value: 'Client' });
    }
  }

  const filteredMatterTypes = useMemo(
    () =>
      integration.matterTypes.filter(
        (mt) => selectedStateOfMatter === (mt as { location?: string }).location
      ),
    [integration.matterTypes, selectedStateOfMatter]
  );

  // Auto-select matter type from LPR hints (preconfigured estate-admin only).
  // Same prev-render guard pattern so the LPR-derived id is available on the
  // same render that downstream emits pick it up.
  const [autoSelectKey, setAutoSelectKey] = useState<string | null>(null);
  const autoSelectThisRender = `${selectedStateOfMatter}|${lprAutoSelectHint?.hasExecutor ?? ''}|${lprAutoSelectHint?.hasAdmin ?? ''}`;
  if (autoSelectKey !== autoSelectThisRender) {
    setAutoSelectKey(autoSelectThisRender);
    if (
      !isDynamicMatterType &&
      lprAutoSelectHint &&
      selectedStateOfMatter &&
      !form.matterTypeId
    ) {
      if (lprAutoSelectHint.hasExecutor) {
        const match = filteredMatterTypes.find((mt) =>
          mt.name.toLowerCase().includes('probate')
        );
        if (match) dispatchForm({ type: 'SET_MATTER_TYPE', id: match.id });
      } else if (lprAutoSelectHint.hasAdmin) {
        const match = filteredMatterTypes.find((mt) =>
          mt.name.toLowerCase().includes('letters')
        );
        if (match) dispatchForm({ type: 'SET_MATTER_TYPE', id: match.id });
      }
    }
  }

  const isClientRoleAutoSelected =
    clientRoles.roles.length === 1 && clientRoles.roles[0] === 'Client';
  const hasNoClientRoles =
    clientRoles.roles.length === 0 && !clientRoles.isLoading;

  // Whether the picker can offer anything at all.
  //
  // `integration.matterTypes` is the preconfigured catalogue the backend resolves
  // by exact slug match against its static table, so it is empty for any Vale
  // matter type with no Smokeball counterpart. The dynamic ("other") path never
  // preloads a catalogue by construction — its options come from the live search
  // below — so it is always selectable once the integration is on.
  const hasSelectableMatterTypes =
    isDynamicMatterType || integration.matterTypes.length > 0;

  // Stable refs so the emit effect's dep list stays narrow and doesn't re-run
  // every time the parent reassigns its handlers.
  const onConfigChangeRef = useRef(onConfigChange);
  const onIntegrationStatusRef = useRef(onIntegrationStatus);
  const onHasSelectableMatterTypesRef = useRef(onHasSelectableMatterTypes);
  onConfigChangeRef.current = onConfigChange;
  onIntegrationStatusRef.current = onIntegrationStatus;
  onHasSelectableMatterTypesRef.current = onHasSelectableMatterTypes;

  useEffect(() => {
    if (integration.status === 'loading') return;

    const integrationEnabled = integration.status === 'enabled';
    onIntegrationStatusRef.current?.(integrationEnabled);

    if (!integrationEnabled) {
      onHasSelectableMatterTypesRef.current?.(false);
      onConfigChangeRef.current(null);
      return;
    }

    onHasSelectableMatterTypesRef.current?.(hasSelectableMatterTypes);

    const clientRoleResolved = hasNoClientRoles || form.representativeOption;
    const isSyncReady = form.matterTypeId && clientRoleResolved;

    if (isSyncReady && selectedStateOfMatter) {
      onConfigChangeRef.current({
        stateOfMatter: selectedStateOfMatter,
        matterTypeId: form.matterTypeId,
        representativeOption: !hasNoClientRoles
          ? form.representativeOption
          : undefined
      });
    } else {
      onConfigChangeRef.current(null);
    }
  }, [
    integration.status,
    hasSelectableMatterTypes,
    selectedStateOfMatter,
    form.matterTypeId,
    form.representativeOption,
    hasNoClientRoles
  ]);

  if (integration.status === 'loading') return null;
  if (integration.status === 'disabled') return null;
  if (integration.status === 'error') {
    return (
      <p className="text-destructive mt-6 w-full text-center text-xs">
        Error verifying integration status
      </p>
    );
  }

  const isCreate = variant === 'create';
  const fieldHeight = isCreate ? 'h-8' : 'h-9';
  const fieldBg = isCreate ? 'border-0 bg-[var(--input-bg)]' : '';

  return (
    <div className={cn('grid grid-cols-2', isCreate ? 'gap-3' : 'gap-4')}>
      {/* State of Matter */}
      <div className="space-y-1.5">
        <label className="text-foreground text-xs font-medium">
          State of Matter
        </label>
        <Select
          value={selectedStateOfMatter}
          onValueChange={(value) =>
            dispatchForm({ type: 'SET_STATE_OF_MATTER', value })
          }
        >
          <SelectTrigger className={cn(fieldHeight, 'text-sm', fieldBg)}>
            <SelectValue placeholder="Select state" />
          </SelectTrigger>
          <SelectContent>
            {AUSTRALIAN_STATES.map((state) => (
              <SelectItem key={state.value} value={state.value}>
                {state.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Matter Type */}
      <div className="space-y-1.5">
        <label className="text-foreground text-xs font-medium">
          Smokeball Matter Type
        </label>
        {isDynamicMatterType ? (
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={popoverOpen}
                className={cn(
                  'w-full justify-between text-sm font-normal',
                  fieldHeight,
                  fieldBg
                )}
                disabled={!selectedStateOfMatter}
              >
                <span className="truncate">
                  {form.matterTypeName || (
                    <span className="text-muted-foreground">
                      {!selectedStateOfMatter
                        ? 'Select state first'
                        : 'Search matter types...'}
                    </span>
                  )}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search matter types..."
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList>
                  {search.isSearching ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  ) : (
                    <>
                      <CommandEmpty>No matter types found.</CommandEmpty>
                      <CommandGroup>
                        {search.results.map((type) => (
                          <CommandItem
                            key={type.id}
                            value={type.id}
                            onSelect={() => {
                              dispatchForm({
                                type: 'SET_MATTER_TYPE',
                                id: type.id,
                                name: type.name
                              });
                              setPopoverOpen(false);
                              setSearchQuery('');
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                form.matterTypeId === type.id
                                  ? 'opacity-100'
                                  : 'opacity-0'
                              )}
                            />
                            {type.category} -{' '}
                            {type.name.replace(`${type.category} -`, '')}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : (
          <Select
            value={form.matterTypeId}
            onValueChange={(id) =>
              dispatchForm({ type: 'SET_MATTER_TYPE', id })
            }
            disabled={!selectedStateOfMatter}
          >
            <SelectTrigger className={cn(fieldHeight, 'text-sm', fieldBg)}>
              <SelectValue
                placeholder={
                  !selectedStateOfMatter
                    ? 'Select state first'
                    : 'Select matter type'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {filteredMatterTypes.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.category} -{' '}
                  {type.name.replace(`${type.category} -`, '')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Client Role */}
      {!clientRoles.isLoading &&
        !isClientRoleAutoSelected &&
        !hasNoClientRoles && (
          <div className="col-span-2 space-y-1.5">
            <label className="text-foreground text-xs font-medium">
              Client Role
            </label>
            <Select
              value={form.representativeOption}
              onValueChange={(value) =>
                dispatchForm({ type: 'SET_REPRESENTATIVE_OPTION', value })
              }
              disabled={clientRoles.isLoading || clientRoles.roles.length === 0}
            >
              <SelectTrigger className={cn(fieldHeight, 'text-sm', fieldBg)}>
                <SelectValue
                  placeholder={
                    clientRoles.isLoading
                      ? 'Loading roles...'
                      : 'Select client role'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {clientRoles.roles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
    </div>
  );
}
